import type { Account } from 'app-components/flux/accounts/accountsTypes';

export type GroupingData = {
  id: string;
  displayLabel: string;
  displayOrder: number;
  svgIconId?: string;
  routeTo?: string | null;
  balanceType?: string;
};

export function getL1GroupingData(account: Account): GroupingData {
  const uniqueId = account.businessId ? `-${account.businessId}` : '';
  const operationalGroup: GroupingData = {
    id: `OPERATIONAL${uniqueId}`,
    displayLabel: 'Business Payments',
    displayOrder: 6,
    svgIconId: 'IconBanking',
    routeTo: '/transactions',
  };
  switch (account.type) {
    case 'BANK':
    case 'CASH':
    case 'CREDIT':
      return {
        id: `BANKING${uniqueId}`,
        displayLabel: 'Banking',
        displayOrder: 1,
        svgIconId: 'IconBanking',
        routeTo: '/transactions',
      };
    case 'INVESTMENT':
      return {
        id: `INVESTMENT${uniqueId}`,
        displayLabel: 'Investments',
        displayOrder: 2,
        svgIconId: 'IconInvestment',
        routeTo: '/investing?selectedTab=portfolio',
        balanceType: 'today',
      };
    case 'REAL_ESTATE':
    case 'VEHICLE':
      return {
        id: `ASSET${uniqueId}`,
        displayLabel: 'Assets',
        displayOrder: 3,
        svgIconId: 'IconLoan',
        routeTo: '/transactions',
      };
    case 'OTHER_ASSET':
      // TODO: Remove the UNKNOWN when we fix UNDEPOSITED_FUNDS on QCS
      if (account.subType === 'UNDEPOSITED_FUNDS' || account.subType === 'UNKNOWN') {
        return operationalGroup;
      }
      return {
        id: `ASSET${uniqueId}`,
        displayLabel: 'Assets',
        displayOrder: 4,
        svgIconId: 'IconLoan',
        routeTo: '/transactions',
      };
    case 'LOAN':
    case 'OTHER_LIABILITY':
      return {
        id: `LIABILITY${uniqueId}`,
        displayLabel: 'Liabilities',
        displayOrder: 5,
        svgIconId: 'IconLoan',
        routeTo: '/transactions',
      };
    case 'ACCOUNTS_RECEIVABLE':
    case 'ACCOUNTS_PAYABLE':
    case 'PAYMENT':
      return operationalGroup;
    case 'GOAL':
      return {
        id: `GOALS${uniqueId}`,
        displayLabel: 'Savings Goals',
        displayOrder: 7,
        svgIconId: 'IconLoan',
        routeTo: '/goals',
      };
    default:
      // assert(false, `Unexpected account type => ${account.type}`);
      return {
        id: `OTHER${uniqueId}`,
        displayLabel: 'Other',
        displayOrder: 8,
        svgIconId: 'IconBanking',
        routeTo: '/transactions',
      };
  }
}

const l2GroupingIds = ['BANKING', 'INVESTMENT', 'ASSET', 'LIABILITY'] as const;
export type L2GroupingId = (typeof l2GroupingIds)[number];
export const l2Substrings: L2GroupingId[] = [...l2GroupingIds];

export function hasL2Grouping(id: string, numberOfAccounts: number): boolean {
  // ids can now be for ex ASSET or ASSET-123456789 (some businessId)
  if (l2Substrings.some((accountType) => id.includes(accountType))) {
    return numberOfAccounts > 0;
  }
  return false;
}

export function getL2GroupingData(
  node: { routeTo?: string | null } | null | undefined,
  account: Account,
  ignoreHiddenGrouping = false,
): GroupingData {
  const uniqueId = account.businessId ? `-${account.businessId}` : '';
  const subType: string | null | undefined = account.subType;
  if (account.isExcludedFromAccountBar && !ignoreHiddenGrouping) {
    return {
      id: `HIDDEN${uniqueId}`,
      displayLabel: 'Hidden',
      displayOrder: 100,
      routeTo: node?.routeTo,
    };
  }

  switch (account.type) {
    case 'BANK':
    case 'CASH':
    case 'CREDIT':
      if (
        account.type === 'CASH' ||
        subType === 'CHECKING' ||
        subType === 'DIGITAL_CASH' ||
        subType === 'CASH_MANAGEMENT'
      ) {
        return {
          id: `CASH_CHECKING${uniqueId}`,
          displayLabel: 'Cash & Checking',
          displayOrder: 1,
          routeTo: node?.routeTo,
        };
      }
      if (account.type === 'CREDIT') {
        return {
          id: `CREDIT${uniqueId}`,
          displayLabel: 'Credit',
          displayOrder: 2,
          routeTo: node?.routeTo,
        };
      }
      if (subType === 'SAVINGS' || subType === 'CD' || subType === 'MONEY_MARKET') {
        return {
          id: `SAVINGS${uniqueId}`,
          displayLabel: 'Savings',
          displayOrder: 3,
          routeTo: node?.routeTo,
        };
      }
      if (subType === 'TRUST' || subType === 'RENTS_HELD_IN_TRUST') {
        return {
          id: `FIDUCIARY${uniqueId}`,
          displayLabel: 'Fiduciary',
          displayOrder: 4,
          routeTo: node?.routeTo,
        };
      }
      return {
        id: `OTHER_BANKING${uniqueId}`,
        displayLabel: 'Other Banking',
        displayOrder: 5,
        routeTo: node?.routeTo,
      };
    case 'INVESTMENT':
      switch (subType) {
        case 'BROKERAGE':
        case 'UGMA_UTMA':
          return {
            id: `BROKERAGE${uniqueId}`,
            displayLabel: 'Brokerage',
            displayOrder: 6,
            routeTo: node?.routeTo,
            balanceType: 'today',
          };
        case '401K':
        case '403B':
        case 'IRA':
        case 'KEOGH':
        case 'ROTH_401K':
        case 'ROTH_IRA':
        case 'SEP_IRA':
        case 'SIMPLE_IRA':
          return {
            id: `RETIREMENT${uniqueId}`,
            displayLabel: 'Retirement',
            displayOrder: 7,
            routeTo: node?.routeTo,
            balanceType: 'today',
          };
        case '529':
          return {
            id: `EDUCATION${uniqueId}`,
            displayLabel: 'Education',
            displayOrder: 8,
            routeTo: node?.routeTo,
            balanceType: 'today',
          };
        case 'OTHER_INVESTMENT':
        default:
          return {
            id: `OTHER_INVESTMENT${uniqueId}`,
            displayLabel: 'Other Investments',
            displayOrder: 9,
            routeTo: node?.routeTo,
            balanceType: 'today',
          };
      }
    case 'REAL_ESTATE':
      return {
        id: `REAL_ESTATE${uniqueId}`,
        displayLabel: 'Real Estate',
        displayOrder: 10,
        routeTo: node?.routeTo,
      };
    case 'VEHICLE':
      return {
        id: `VEHICLE${uniqueId}`,
        displayLabel: 'Vehicle',
        displayOrder: 11,
        routeTo: node?.routeTo,
      };
    case 'OTHER_ASSET':
      return {
        id: `OTHER_ASSET${uniqueId}`,
        displayLabel: 'Other Assets',
        displayOrder: 12,
        routeTo: node?.routeTo,
      };
    case 'LOAN':
    case 'OTHER_LIABILITY':
      switch (subType) {
        case 'CONSTRUCTION_LOAN':
        case 'HOME_EQUITY':
        case 'MORTGAGE':
          return {
            id: `HOME${uniqueId}`,
            displayLabel: 'Home Loan',
            displayOrder: 13,
            routeTo: node?.routeTo,
          };
        case 'AUTO_LOAN':
          return {
            id: `AUTO_LOAN${uniqueId}`,
            displayLabel: 'Vehicle Loan',
            displayOrder: 14,
            routeTo: node?.routeTo,
          };
        case 'CONSUMER_LOAN':
        case 'MILITARY_LOAN':
        case 'STUDENT_LOAN':
          return {
            id: `PERSONAL_LOAN${uniqueId}`,
            displayLabel: 'Personal Loan',
            displayOrder: 15,
            routeTo: node?.routeTo,
          };
        case 'TRUST_LIABILITY':
        case 'RENTS_HELD_IN_TRUST_LIABILITY':
          return {
            id: `FIDUCIARY_LIABILITY${uniqueId}`,
            displayLabel: 'Fiduciary Liabilities',
            displayOrder: 16,
            routeTo: node?.routeTo,
          };
        default:
          return {
            id: `OTHER_LIABILITY${uniqueId}`,
            displayLabel: 'Other Liabilities',
            displayOrder: 17,
            routeTo: node?.routeTo,
          };
      }
    default:
      return {
        id: `OTHER${uniqueId}`,
        displayLabel: 'Other',
        displayOrder: 18,
        routeTo: node?.routeTo,
      };
  }
}

export const nodeAssetIds = [
  'CASH_CHECKING',
  'SAVINGS',
  'DIGITAL_CASH',
  'CASH_MANAGEMENT',
  'OTHER_BANKING',
  'BROKERAGE',
  'RETIREMENT',
  'EDUCATION',
  'FIDUCIARY',
  'OTHER_INVESTMENT',
  'REAL_ESTATE',
  'VEHICLE',
  'ASSET',
  'OTHER',
  'OTHER_ASSET',
];

export const nodeDebtIds = [
  'CREDIT',
  'HOME',
  'PERSONAL_LOAN',
  'AUTO_LOAN',
  'OTHER_LOAN',
  'FIDUCIARY_LIABILITY',
  'OTHER_LIABILITY',
];

// Maps local account-node IDs to the entity IDs QCS uses in net worth report rows.
// QCS uses display-label-style strings (spaces/ampersands) rather than underscored identifiers.
// IDs that match exactly (e.g. SAVINGS, BROKERAGE) are omitted — the identity fallback handles them.
export const nodeIdToNetWorthEntityId: Partial<Record<string, string>> = {
  CASH_CHECKING: 'CASH & CHECKING',
  OTHER_BANKING: 'OTHER BANKING',
  OTHER_INVESTMENT: 'OTHER INVESTMENTS',
  REAL_ESTATE: 'REAL ESTATE',
  HOME: 'HOME LOAN',
  PERSONAL_LOAN: 'PERSONAL LOAN',
  AUTO_LOAN: 'VEHICLE LOAN',
  OTHER_ASSET: 'OTHER',
  OTHER_LIABILITY: 'OTHER LIABILITIES',
  CREDIT: 'CREDIT CARDS',
};
